import {
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/system'
import type { GetServerSideProps } from 'next'
import { signIn } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import Feedback from '../components/Feedback'
import Hero from '../components/Hero'
import HeroCarousel from '../components/HeroCarousel'
import commonTranslations from '../constants/commonTranslations'
import { useGetUserVerificationDataByUserIdQuery } from '../generated/graphql'
import useAuthClaims from '../hooks/useAuthClaims'
import COLORS from '../styles/colors'
import { NextPageWithLayout } from '../types/global'
import { getStartPageByRole } from '../util/getStartPageByRole'

type HomeProps = {
  heroCarouselLocale: 'da' | 'en'
}

const Home: NextPageWithLayout = ({ heroCarouselLocale }: HomeProps) => {
  const { t } = useTranslation(['home'])
  const router = useRouter()
  const {
    claims,
    isAuthenticated,
    loading: claimsLoading,
    userId,
  } = useAuthClaims()

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useGetUserVerificationDataByUserIdQuery({
    fetchPolicy: 'no-cache',
    skip: !userId,
    variables: {
      id: userId,
    },
  })
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)

  const user = userData?.user_by_pk || undefined
  const person = user?.person

  const emailVerified = person?.emailverified
  const role = claims?.role
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))

  const loading = claimsLoading || userLoading

  useEffect(() => {
    if (
      !isRedirecting &&
      !loading &&
      isAuthenticated &&
      emailVerified &&
      role !== 'guest'
    ) {
      if (!person?.name || !person.person_get_has_accepted_latest_eula) {
        router.push('/onboarding')
        setIsRedirecting(true)
      } else {
        router.push(getStartPageByRole(claims?.role))
        setIsRedirecting(true)
      }
    }
  }, [emailVerified, loading, isAuthenticated, role])

  return (
    <>
      <Head>
        <title>{t('pageTitle', 'Human Library')}</title>
      </Head>
      {isRedirecting || loading ? (
        <Container maxWidth="xl" sx={{ minHeight: '70vh' }}>
          <Feedback type="loading" />
        </Container>
      ) : (
        <>
          {!isAuthenticated ? (
            <HeroCarousel locale={heroCarouselLocale} />
          ) : (
            <Hero imageSrc="/hero_unjudge.png" />
          )}
          <Container maxWidth="xl" component="main" id="main-content">
            <Stack
              alignItems="center"
              spacing="80px"
              paddingBottom="100px"
              width="100%"
              sx={{
                background: 'url(/background_overlay.svg)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPositionX: 'right',
                backgroundPositionY: 'top 46px',
              }}
            >
              <Box
                sx={{
                  pt: '86px',
                  paddingBottom: '2rem',
                  textAlign: 'center',
                }}
              >
                <Stack alignItems="center">
                  <Stack alignItems="center" spacing="8px">
                    <Typography
                      variant={isSmall ? 'garage_h3' : 'garage_h2'}
                      component="h1"
                      color={COLORS.greyDark}
                    >
                      {t('header')}
                    </Typography>
                    <Typography
                      variant="inter_bodyText"
                      color={COLORS.greyDark}
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {t('subHeader')}
                    </Typography>
                    <Typography
                      variant="inter_bodyText"
                      color={COLORS.greyDark}
                    >
                      {!isAuthenticated && t('notSignedInText')}
                      {isAuthenticated &&
                        !emailVerified &&
                        t('Email not verified title')}

                      {isAuthenticated &&
                        emailVerified &&
                        claims?.role === 'guest' &&
                        t('Guest welcome title')}
                    </Typography>
                  </Stack>
                  <Stack
                    spacing="4px"
                    alignItems="center"
                    textAlign="center"
                    mt="40px"
                  >
                    {!isAuthenticated && (
                      <Button
                        sx={{ width: ['220px', '320px'], mt: '40px' }}
                        variant="contained"
                        color="secondary"
                        onClick={() => signIn('auth0')}
                      >
                        {t('Please log in')}
                      </Button>
                    )}
                    {isAuthenticated && !emailVerified && (
                      <Typography variant="inter_bodyText">
                        {t('Email not verified')}
                      </Typography>
                    )}
                    {isAuthenticated &&
                      claims &&
                      emailVerified &&
                      ['guest'].includes(claims.role) && (
                        <Typography variant="inter_bodyText">
                          {t('Guest welcome message')}
                        </Typography>
                      )}
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Container>
        </>
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  defaultLocale,
  locale,
}) => {
  const resolvedLocale = (locale || defaultLocale || 'en').toLowerCase()
  const heroCarouselLocale: 'da' | 'en' = resolvedLocale === 'da' ? 'da' : 'en'

  return {
    props: {
      ...(await serverSideTranslations(heroCarouselLocale, [
        'home',
        'Footer',
        'Feedback',
        'HeroCarousel',
        'ReadingFilters',
        ...commonTranslations,
      ])),
      heroCarouselLocale,
    },
  }
}

export default Home
